/**
 * AlertHighlightChip
 *
 * Pill flotante centrada al VIEWPORT que indica cuántas filas están resaltadas
 * por una Smart Alert del dashboard. Click → limpia el highlight.
 *
 * Diseño: pill blanca con borde amarillo warning, dot mini de color, sombra
 * sutil, animación pulse profesional (scale 1 → 1.08).
 *
 * Centrado real: usa React Portal a `document.body` para evitar que cualquier
 * ancestor con `transform`/`filter` rompa el `position: fixed`. El `top` se
 * calcula dinámicamente para alinearse con el anchor (header de tabla).
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const LABELS = {
  vencidas: 'vencidas',
  por_vencer_7d: 'vencen <7d'
};

const DOT_COLORS = {
  vencidas: 'bg-rose-400',
  por_vencer_7d: 'bg-amber-400'
};

export default function AlertHighlightChip({ highlightKey, hasMatches, count, onClose, anchorRef }) {
  // Top inicial visible (cerca del top de la pantalla). Después se ajusta al anchor.
  const [top, setTop] = useState('110px');

  // Medir el top del anchor (header de la tabla) — con retry para esperar a que
  // el DOM esté listo, y ResizeObserver para detectar cambios de tamaño.
  useEffect(() => {
    if (!highlightKey || !hasMatches) return;
    let cancelled = false;
    let observer = null;
    let rafId = null;

    const measure = () => {
      if (cancelled) return;
      const el = anchorRef?.current;
      if (!el) {
        rafId = requestAnimationFrame(measure);
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.height === 0) {
        rafId = requestAnimationFrame(measure);
        return;
      }
      setTop(`${rect.top + Math.min(rect.height / 2, 20)}px`);

      if (!observer) {
        observer = new ResizeObserver(() => {
          if (cancelled || !anchorRef?.current) return;
          const r = anchorRef.current.getBoundingClientRect();
          setTop(`${r.top + Math.min(r.height / 2, 20)}px`);
        });
        observer.observe(el);
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [highlightKey, hasMatches, anchorRef]);

  if (!highlightKey || !hasMatches) return null;

  // Portal al body — esto saca el chip de cualquier stacking context con
  // transform/filter que rompería el `position: fixed` y por ende el centrado.
  // Centrado respecto al ÁREA DE CONTENIDO (alineado con el título de la página):
  // en lg+ compensamos el sidebar (lg:w-72 = 288px → mitad = 144px) sumándolo al
  // 50% del viewport. En mobile sin sidebar, queda en 50% normal.
  return createPortal(
    <button
      onClick={onClose}
      style={{
        position: 'fixed',
        top: top,
        // left vive en className para poder usar breakpoint lg: con compensación de sidebar.
        // El transform `translate(-50%, -50%)` vive en el keyframe `chip-pulse` para
        // centrar el chip respecto al punto de left/top y a la vez animar el scale.
        zIndex: 9999,
        willChange: 'transform'
      }}
      className="left-1/2 lg:left-[calc(50%+144px)] animate-chip-pulse flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-full text-[11px] text-gray-700 hover:bg-amber-50 hover:border-amber-400 shadow-md transition-colors"
      title={`${count} factura(s) resaltada(s) desde la alerta del dashboard. Click para limpiar.`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${DOT_COLORS[highlightKey] || 'bg-amber-400'}`}></span>
      <span><strong className="font-semibold">{count}</strong> {LABELS[highlightKey] || highlightKey}</span>
      <X className="h-3 w-3 text-gray-400" />
    </button>,
    document.body
  );
}
